<template>
    <div class="bgrid">
        <b-filter-modal
            :isShowFilterEditor="isShowFilterEditor"
            v-if="canShowFilter"
            :filterOptions="filterOptions"
            :filterableFields="filterableFields"
            @filterChanged="applyFilters"
            @dismissModal="handleDismiss"></b-filter-modal>
        <b-table-header
            :value="props.showPaging ? tableOptions : null"
            @optionChange="handleOptionChange"
            header-slot-class="b-header-slot w-100 position-relative"
            :current-item-count="numberOfReturnedItems">
            <div class="button-wrapper">
                <slot name="beforeFilter"> </slot>
                <button
                    @click.prevent="showFilterEditor"
                    v-if="canShowFilter"
                    class="btn btn-primary filter-button-width">
                    <i
                        class="bi bi-search me-1"
                        aria-hidden="true"></i>
                    Filter
                </button>
            </div>
            <slot name="header.toolbar"></slot>
            <div
                class="ms-2 filter-description"
                v-if="filtersDescription">
                <span>
                    {{ filtersDescription }}
                </span>
                <button
                    class="btn-close"
                    aria-label="Remove all currently applied filters."
                    v-tooltip="`Remove all currently applied filters.`"
                    @click.prevent="resetFilters()"></button>
            </div>
            <template v-slot:pagingStart>
                <slot name="pagingStart"> </slot>
            </template>
        </b-table-header>

        <b-table
            :items="itemsToDisplay"
            :columns="columns"
            :caption="caption"
            :value="tableOptions"
            @optionChange="handleOptionChange">
            <template
                v-for="column in columns"
                :key="column.key"
                v-slot:[`header.${column.key}`]>
                <slot
                    :name="`header.${column.key}`"
                    v-bind:column="column">
                </slot>
            </template>

            <template
                v-for="column in columns"
                :key="column.key"
                v-slot:[`cell.${column.key}`]="{item}">
                <slot
                    :name="`cell.${column.key}`"
                    v-bind:column="column"
                    v-bind:item="item"></slot>
            </template>
        </b-table>
        <b-table-header
            v-if="showPaging"
            :value="tableOptions"
            @optionChange="handleOptionChange"
            header-slot-class="b-header-slot w-100"
            :current-item-count="numberOfReturnedItems">
        </b-table-header>
        <loading-spinner :show-loading="loading"></loading-spinner>
    </div>
</template>

<script lang="ts" setup>
import BTable from "../BTable/BTable.vue";
import BTableHeader from "../BTable/BTableHeader.vue";
import BFilterModal from "../BFilterEditor/BFilterModal.vue";
import LoadingSpinner from "../Misc/LoadingSpinner.vue";
import {computed, ref} from "vue";
import {BTableColumn, BTableOptions} from "../BTable/BTable.types";
import {BFilterableField, BFilterOptions, describeFilters} from "../BFilterEditor/BFilterEditor.types";

import {useSlots} from "vue";
const slots = useSlots();

const props = withDefaults(
    defineProps<{
        caption?: string;
        columns: BTableColumn[];
        tableOptions?: BTableOptions;
        filterOptions?: BFilterOptions;
        filterableFields?: BFilterableField[];
        items?: any[];
        loading?: boolean;
        showFilter?: boolean;
        showPaging?: boolean;
    }>(),
    {
        showPaging: true,
        showFilter: true,
        loading: false,
    }
);

const isShowFilterEditor = ref(false);
const hasAppliedFilters = ref(props.filterOptions != null && props.filterOptions.filters.length);

const canShowFilter = computed(() => {
    return props.showFilter && props.filterableFields && props.filterableFields.length > 0;
});

const numberOfReturnedItems = computed(() => {
    return props.items?.length;
});

const itemsToDisplay = computed(()=> {
    if(props.showPaging && props.tableOptions?.pagingMode == "noTotal"){
        //When were hiding the total text,first button, and last button then we return itemsPerPage + 1 in our get data call.
        //If the returned amount of rows is greater than itemsPerPage then enable the next button otherwise disable the next button to prevent an error
        return props.items?.slice(0, props.tableOptions.itemsPerPage);
    } 
    
    return props.items;
});
const emit = defineEmits(["optionChange"]);

const showFilterEditor = function () {
    isShowFilterEditor.value = true;
};
const handleDismiss = function () {
    isShowFilterEditor.value = false;
};
const handleOptionChange = async function (options: BTableOptions) {
    await getData();
};

const getData = async () => {
    emit("optionChange");
};

const filtersDescription = computed(() => {
    if (props.filterOptions == null) {
        return "";
    }
    return describeFilters(props.filterOptions.filters, props.filterableFields!);
});

const resetFilters = async () => {
    if (props.filterOptions != null) {
        props.filterOptions.filters = [];
        props.tableOptions.page = 1;
        await getData();
    }
    isShowFilterEditor.value = false;
    hasAppliedFilters.value = false;
};

const applyFilters = async (newFilterOptions: BFilterOptions) => {
    if (props.filterOptions) {
        props.filterOptions.filters = newFilterOptions.filters;
        props.tableOptions.page = 1;
    }

    isShowFilterEditor.value = false;
    await getData();
};
</script>
<style lang="scss">
.bgrid .filter-description {
    flex-grow: 1;
    flex-shrink: 0;
    flex-basis: 0%;
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
}
</style>
